import React, { useEffect, useMemo, useState } from "react";
import Header from "../Components/Header";
import { db } from "../firebase/config";
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore";
import {
  getStorage,
  ref,
  deleteObject,
} from "firebase/storage";
import toast from "react-hot-toast";
import CreateMegnetVideoModal from "../Components/CreateMegnetVideoModal";
import { ExternalLink, Pencil, Search, Trash2, Plus } from "lucide-react";

function extractYouTubeId(url) {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    if (parsed.hostname.includes("youtu.be")) {
      return parsed.pathname.replace("/", "") || null;
    }
    if (parsed.hostname.includes("youtube.com")) {
      const fromQuery = parsed.searchParams.get("v");
      if (fromQuery) return fromQuery;
      const parts = parsed.pathname.split("/").filter(Boolean);
      const embedIndex = parts.indexOf("embed");
      if (embedIndex !== -1 && parts[embedIndex + 1]) {
        return parts[embedIndex + 1];
      }
    }
  } catch {
    return null;
  }
  return null;
}

function getThumbnailUrl(video) {
  if (video?.thumbnailUrl) return video.thumbnailUrl;
  const videoId = extractYouTubeId(video?.videoUrl);
  if (!videoId) return null;
  return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
}

function plainTextDescription(value) {
  const text = String(value ?? "").replace(/<[^>]+>/g, "").trim();
  if (!text) return "-";
  return text.length > 90 ? `${text.slice(0, 90)}...` : text;
}

export default function MegnetsVideos({ isSidebarOpen, setIsSidebarOpen }) {
  const [videos, setVideos] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [videoToEdit, setVideoToEdit] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const videosPerPage = 10;

  const handleConfirmDelete = async () => {
    if (!deleteTarget?.id) return;
    setDeleteSubmitting(true);
   try{
    const docId = deleteTarget.id;
    if(deleteTarget.uploadType === "upload" && deleteTarget.contentUrl){
      await deleteFromStorage(`videos/${docId}/video/video.mp4`);
    }
    if (deleteTarget.thumbnailUrl) {
      await deleteFromStorage(`videos/${docId}/thumbnail/thumbnail.jpg`);
    }
    await deleteDoc(doc(db, "MegnetsVideos", docId));
    toast.success("Video deleted.");
    setDeleteTarget(null);
   } catch (err) {
      console.error("[MegnetsVideos] delete", err);
      toast.error(err?.message || "Could not delete video.");
    } finally {
      setDeleteSubmitting(false);
    }
  };
  async function deleteFromStorage(storagePath) {
    try {
      const storage = getStorage();
      const storageRef = ref(storage, storagePath);
      await deleteObject(storageRef);
    } catch (err) {
      console.warn("[deleteFromStorage] could not delete:", storagePath, err?.code);
    }
  }

  useEffect(() => {
    const q = query(collection(db, "MegnetsVideos"), orderBy("order", "asc"));
    console.log("q", q);
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const list = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      console.log("list", list);
      setVideos(list);
    });
    return () => unsubscribe();
  }, []);

  const filteredVideos = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase();
    if (!needle) return videos;
    return videos.filter((v) =>
      String(v.title ?? "").toLowerCase().includes(needle)
    );
  }, [searchTerm, videos]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredVideos.length / videosPerPage));
  const safePage = Math.min(currentPage, totalPages);
  const indexOfLast = safePage * videosPerPage;
  const indexOfFirst = indexOfLast - videosPerPage;
  const currentVideos = filteredVideos.slice(indexOfFirst, indexOfLast);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  return (
    <div className="w-full rounded-sm h-full">
      <div className="flex flex-col">
        <Header
          PageName={"Videos"}
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
        />
      </div>

      <div className="rounded-sm">
        <div className="w-full p-6 bg-[#081028] bg-primary opacity-75 ">
         
          <div className="flex items-center justify-between mb-8 gap-4">
            <h1 className="text-2xl font-black  tracking-tight text-white">SUMMARY</h1>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  type="text"
                  placeholder="Search overview..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="bg-slate-800/60 border border-slate-700/50  pl-9 pr-4 py-2.5 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-blue-500/60 transition-colors w-56"
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  setVideoToEdit(null);
                  setIsCreateModalOpen(true);
                }}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-bold text-sm px-4 py-2.5 rounded-xl shadow-lg transition-colors"
              >
                <Plus size={18} strokeWidth={2.5} />
                Create Video
              </button>
            </div>
          </div>

          <div className="glass-card bg-[#0B1739]  overflow-hidden border border-slate-700/40 
          
          ">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-700/50 bg-slate-800/30">
                    <th className="text-left text-xs font-bold text-slate-400 uppercase tracking-widest px-4 py-4 w-12">
                    ORDER
                    </th>
                    <th className="text-left text-xs font-bold text-slate-400 uppercase tracking-widest pl-12 py-4 ">
                    TITLE
                    </th>
                    <th className="text-left text-xs font-bold text-slate-400 uppercase tracking-widest px-4 py-4">
                    DESCRIPTION
                    </th>
                    <th className="text-left text-xs font-bold text-slate-400 uppercase tracking-widest px-4 py-4">
                    CONTENT URL
                    </th>
                    <th className="text-left text-xs font-bold text-slate-400 uppercase tracking-widest px-4 py-4">
                    ORRA THUMBNAIL
                    </th>
                    <th className="text-left text-xs font-bold text-slate-400 uppercase tracking-widest pl-12 py-4">
                    STATUS
                    </th>
                    <th className="text-left text-xs font-bold text-slate-400 uppercase tracking-widest px-4 py-4">
                      ACTIONS
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/60">
                  {currentVideos.map((video) => {
                    const thumbnail = getThumbnailUrl(video);
                    return (
                    <tr
                      key={video.id}
                      className="hover:bg-slate-800/20 transition-colors group"
                    >
                      <td className="px-4 py-4 ">
                        <div className="w-8 h-8 rounded-full bg-slate-700/60 flex items-center justify-center text-slate-300 font-black text-sm">
                          {video.order ?? "-"}
                        </div>
                      </td>
                      <td className="px-4 py-4  max-w-[150px]">
                        <div className="font-semibold text-slate-200 leading-snug line-clamp-2 text-xs">
                          {video.title ?? "-"}
                        </div>
                      </td>
                      <td className="px-4 py-4 max-w-[240px]">
                        <p className="text-slate-400 text-xs leading-relaxed text-start line-clamp-2">
                          {plainTextDescription(video.description)}
                        </p>
                      </td>
                      <td className="px-4 py-4">
                        {video.videoUrl ? (
                          <a
                            href={video.videoUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-400 hover:text-blue-300 text-xs font-medium flex items-center gap-1 transition-colors"
                          >
                            <span className="truncate max-w-[160px]">
                              {String(video.videoUrl).replace("https://", "")}
                            </span>
                            <ExternalLink size={11} />
                          </a>
                        ) : (
                          <span className="text-slate-500 text-xs">-</span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="w-16 aspect-video rounded-lg overflow-hidden border border-slate-700/50 shadow-md">
                          {thumbnail ? (
                            <img
                              src={thumbnail}
                              alt={video.title ?? "Video thumbnail"}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          ) : (
                            <div className="w-full h-full bg-slate-800/70 flex items-center justify-center text-slate-500 text-[10px]">
                              N/A
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span
                          className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                            video.isPublished === true ||
                            video.isPublished === "success"
                              ? "bg-emerald-600/20 text-emerald-400"
                              : "bg-slate-700/60 text-slate-400"
                          }`}
                        >
                          {video.isPublished === true ||
                          video.isPublished === "success"
                            ? "Success"
                            : "Brouillon"}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              setIsCreateModalOpen(false);
                              setVideoToEdit(video);
                            }}
                            className="flex items-center gap-1.5 bg-slate-700/60 hover:bg-slate-700 text-slate-200 text-xs font-bold px-3 py-1.5 rounded-lg transition-colors"
                          >
                            <Pencil size={12} /> Edit
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setDeleteTarget({
                                id: video.id,
                                title: video.title ?? "",
                              })
                            }
                            className="flex items-center gap-1.5 bg-red-600/20 hover:bg-red-600/40 text-red-400 text-xs font-bold px-3 py-1.5 rounded-lg transition-colors"
                          >
                            <Trash2 size={12} /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  )})}

                  {currentVideos.length === 0 && (
                    <tr>
                      <td colSpan={7} className="text-center text-slate-500 py-12">
                        No videos found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between border-t border-slate-700/40 px-6 py-3 text-xs text-slate-500">
              <span>Rows per page: {videosPerPage}</span>
              <span>
                {filteredVideos.length === 0 ? 0 : indexOfFirst + 1}-
                {Math.min(indexOfLast, filteredVideos.length)} of {filteredVideos.length}
              </span>
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={() => setCurrentPage(1)}
                  disabled={safePage <= 1}
                  className="w-7 h-7 rounded-lg bg-slate-800/50 hover:bg-slate-700 text-slate-400 font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  «
                </button>
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={safePage <= 1}
                  className="w-7 h-7 rounded-lg bg-slate-800/50 hover:bg-slate-700 text-slate-400 font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ‹
                </button>
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage >= totalPages}
                  className="w-7 h-7 rounded-lg bg-slate-800/50 hover:bg-slate-700 text-slate-400 font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ›
                </button>
                <button
                  type="button"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={safePage >= totalPages}
                  className="w-7 h-7 rounded-lg bg-slate-800/50 hover:bg-slate-700 text-slate-400 font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  »
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isCreateModalOpen || videoToEdit ? (
        <CreateMegnetVideoModal
          editVideo={videoToEdit}
          onClose={() => {
            setIsCreateModalOpen(false);
            setVideoToEdit(null);
          }}
        />
      ) : null}

      {deleteTarget ? (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 px-4"
          role="presentation"
          onClick={() => !deleteSubmitting && setDeleteTarget(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-video-title"
            className="w-full max-w-sm rounded-2xl border border-slate-600/80 bg-[#12151c] p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              id="delete-video-title"
              className="text-lg font-bold text-white mb-2"
            >
              Delete video?
            </h3>
            <p className="text-sm text-slate-400 mb-6">
              {deleteTarget.title
                ? `This will remove “${String(deleteTarget.title).slice(0, 80)}${String(deleteTarget.title).length > 80 ? "…" : ""}” permanently.`
                : "This will remove this video permanently."}
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                disabled={deleteSubmitting}
                onClick={() => setDeleteTarget(null)}
                className="text-sm font-semibold text-slate-300 hover:text-white px-3 py-2 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={deleteSubmitting}
                onClick={handleConfirmDelete}
                className="rounded-xl bg-red-600 hover:bg-red-500 disabled:opacity-60 text-white text-sm font-bold px-4 py-2 transition-colors"
              >
                {deleteSubmitting ? "Deleting…" : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

